import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowUpRight, BellRing, Loader2, RefreshCw, Users, Warehouse, Zap, MailWarning } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import PriceChart from "@/components/PriceChart";
import ReportDialog from "@/components/ReportDialog";
import RevenuePanel, { useDeliveryStats } from "@/components/RevenuePanel";
import { MetalsStrip } from "@/pages/Metalle";
import { useApi } from "@/lib/api";
import { dt, eur, num, pct, STATUS_STYLE, KIND_LABEL, unitDigits } from "@/lib/format";
import { useMe } from "@/context/MeContext";

const Kpi = ({ icon: Icon, label, value, testId, to }) => (
  <Link to={to} className="panel group flex flex-col justify-between p-5 fade-up" data-testid={testId}>
    <div className="flex items-center justify-between text-xs uppercase tracking-wider text-slate-500">
      {label}
      <Icon className="h-4 w-4 text-slate-400 transition-colors group-hover:text-orange-500" />
    </div>
    <div className="num mt-4 text-3xl font-bold">{value}</div>
  </Link>
);

const Hero = ({ snap, onRefresh, refreshing }) => {
  const up = snap.change_pct >= 0;
  const Arrow = up ? ArrowUpRight : ArrowDownRight;
  return (
    <div className="copper-glow p-6 sm:p-8 md:col-span-2 lg:col-span-2 fade-up" data-testid="hero-live-price">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-orange-700/80">
            <span className="pulse-dot" /> Kupfer Börsenkurs · COMEX HG
          </div>
          <div className="mt-4 flex flex-wrap items-end gap-4">
            <span className="num text-5xl font-bold tracking-tight sm:text-6xl" data-testid="text-live-copper-price">{num(snap.eur_per_kg, 3)}</span>
            <span className="mb-2 text-lg text-slate-500">€/kg</span>
            <span className={`mb-2 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-sm num ${up ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-600" : "border-red-500/30 bg-red-500/10 text-red-600"}`} data-testid="badge-copper-price-change">
              <Arrow className="h-4 w-4" /> {pct(snap.change_pct)}
            </span>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onRefresh} disabled={refreshing} data-testid="btn-refresh-price" className="text-slate-600">
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Aktualisieren
        </Button>
      </div>
      <div className="mt-8 grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
        {[
          ["Tageshoch", eur(snap.day_high_eur_kg, 3)],
          ["Tagestief", eur(snap.day_low_eur_kg, 3)],
          ["USD / Tonne", `${num(snap.usd_per_t, 0)} $`],
          ["EUR/USD", num(snap.eurusd, 4)],
        ].map(([k, v]) => (
          <div key={k}>
            <div className="text-xs text-slate-500">{k}</div>
            <div className="num mt-1 font-medium">{v}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 text-xs text-slate-500">Börsenzeit: {dt(snap.market_time)} · abgerufen {dt(snap.ts)}</div>
    </div>
  );
};

const LogFeed = ({ logs }) => (
  <div className="panel p-5 lg:row-span-2" data-testid="dashboard-alert-feed">
    <div className="mb-4 flex items-center justify-between">
      <h3 className="text-base font-semibold">Letzte Reports</h3>
      <Link to="/alarme" className="text-xs text-orange-700 hover:underline" data-testid="link-all-logs">Alle</Link>
    </div>
    {logs.length === 0 ? (
      <p className="text-sm text-slate-500">Noch keine Reports versendet.</p>
    ) : (
      <ul className="space-y-3">
        {logs.map((l) => (
          <li key={l.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate font-medium">{l.grade_name}</span>
              <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] uppercase ${STATUS_STYLE[l.status]}`}>{l.status}</span>
            </div>
            <div className="num mt-1 text-xs text-slate-500">{eur(l.price, unitDigits(l.unit))}/{l.unit || "kg"}{l.threshold != null && ` · Ziel ${eur(l.threshold, 2)}`}</div>
            <div className="mt-1 text-[11px] text-slate-500">{dt(l.ts)} · {KIND_LABEL[l.kind] || l.kind}</div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const GradeTable = ({ grades }) => (
  <div className="panel overflow-x-auto p-5 md:col-span-3 lg:col-span-4" data-testid="dashboard-grades-table">
    <div className="mb-4 flex items-center justify-between">
      <h3 className="text-base font-semibold">Richtpreise nach Reinheit</h3>
      <Link to="/kurse" className="text-xs text-orange-700 hover:underline" data-testid="link-all-grades">Rechner & Details</Link>
    </div>
    <table className="w-full min-w-[560px] text-sm">
      <thead className="text-left text-xs uppercase tracking-wider text-slate-500">
        <tr><th className="pb-2">Sorte</th><th className="pb-2">Reinheit</th><th className="pb-2 text-right">Richtpreis €/kg</th><th className="pb-2 text-right">Bestes Hof-Angebot</th></tr>
      </thead>
      <tbody>
        {grades.map((g) => (
          <tr key={g.key} className="border-t border-slate-200" data-testid={`dash-row-${g.key}`}>
            <td className="py-2.5 font-medium">{g.name}</td>
            <td className="py-2.5 text-slate-500">{g.purity}</td>
            <td className="num py-2.5 text-right text-orange-700">{eur(g.price_eur_kg, 3)}</td>
            <td className="py-2.5 text-right text-slate-500">{g.best_yard ? <span><span className="num text-slate-800">{eur(g.best_yard.price)}</span> · {g.best_yard.name}</span> : "–"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function Dashboard() {
  const api = useApi();
  const me = useMe();
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const stats = useDeliveryStats();

  const load = useCallback(() => api.get("/dashboard").then((r) => setData(r.data)).catch(() => toast.error("Dashboard konnte nicht geladen werden")), [api]);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try { await api.post("/prices/refresh"); await load(); toast.success("Kurs aktualisiert"); }
    catch { toast.error("Kurs derzeit nicht verfügbar"); }
    setRefreshing(false);
  };

  if (!data) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>;
  const { snapshot, counts } = data;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Dashboard</h1>
          <p className="mt-1 text-sm text-slate-500">Aktueller Kupfermarkt, Richtpreise und Alarmstatus.</p>
        </div>
        <ReportDialog />
      </div>
      {!data.smtp_configured && me.role === "admin" && (
        <Link to="/einstellungen" className="flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-800" data-testid="smtp-warning-banner">
          <MailWarning className="h-4 w-4 shrink-0" /> E-Mail-Versand (SMTP) ist noch nicht eingerichtet – Alarme werden protokolliert, aber nicht verschickt.
        </Link>
      )}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-4">
        {snapshot ? <Hero snap={snapshot} onRefresh={refresh} refreshing={refreshing} /> : <div className="panel p-8 md:col-span-2 lg:col-span-2 text-slate-500">Kurs derzeit nicht verfügbar.</div>}
        <div className="grid grid-cols-2 gap-4 md:col-span-1 lg:col-span-2">
          <Kpi icon={BellRing} label="Aktive Alarme" value={counts.alerts_active} testId="kpi-alerts-active" to="/alarme" />
          <Kpi icon={Zap} label="Ausgelöst" value={counts.alerts_triggered} testId="kpi-alerts-triggered" to="/alarme" />
          <Kpi icon={Warehouse} label="Wertstoffhöfe" value={counts.yards} testId="kpi-yards" to="/wertstoffhoefe" />
          {me.role === "admin" && <Kpi icon={Users} label="Benutzer" value={counts.users} testId="kpi-users" to="/benutzer" />}
        </div>
        <div className="panel p-5 md:col-span-3 lg:row-span-2">
          <h3 className="mb-2 text-base font-semibold">Kursverlauf Kupfer (€/kg)</h3>
          <PriceChart />
        </div>
        <LogFeed logs={data.recent_logs} />
        <MetalsStrip />
        <RevenuePanel stats={stats} />
        <GradeTable grades={data.grades} />
      </div>
    </div>
  );
}
